import {
    Module, VuexModule, Mutation, Action, getModule, MutationAction,
} from 'vuex-module-decorators';
import Cookies from 'js-cookie';
import store from '@/state/store';
import { authAPI } from '@samclick-static-urls';
import { sendPost, sendGet, sendPut } from '@samclick-api-client';
import { sendUAPost } from '@/api-client/unified-api';
import { _isEmpty } from '@samclick-lodash';
import * as appStorageUtil from '@/utils/localstorage-util';
import Payload from './payload';
import { IS_ADMIN, SHOW_BG } from '@/utils/localstorage-util';
import IntroductionStore from '@/state/modules/introduction';
import i18n from '@/i18next';

function applyAuthToken(token: string, context: any) {
    if (!token) {
        return;
    }

    const isHttps = window.location.protocol === 'https:';
    Cookies.set(appStorageUtil.AUTH_TOKEN, token, {
        secure: isHttps,
        sameSite: 'Strict',
        path: '/',
    });
    Cookies.set(appStorageUtil.AUTH_TOKEN_LEGACY, token, {
        secure: isHttps,
        sameSite: 'Strict',
        path: '/',
    });
    const parsedToken = appStorageUtil.parseJWT(token);
    context.commit('SET_CURRENT_USER_ROLES', parsedToken.scopes || []);
    context.commit('SET_CURRENT_USER_TOKEN', token);
    context.commit('SET_CURRENT_USER_TNC', parsedToken.tnc);
    context.commit('SET_CURRENT_SUBSCRIPTION', parsedToken.subscriptions);
}

function getAuthorizationFromResponse(response: any): string {
    if (!response || !response.headers) {
        return '';
    }
    const headers = response.headers;
    return headers.authorization
        || headers.Authorization
        || headers['authorization']
        || headers['Authorization']
        || '';
}

@Module({
    namespaced: true, dynamic: true, store, name: 'auth',
})
class AuthorizationStore extends VuexModule {
    currentUser: object = !_isEmpty(appStorageUtil.getSessionItem(appStorageUtil.CURRENT_USER))
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ? JSON.parse(appStorageUtil.getSessionItem(appStorageUtil.CURRENT_USER)!) : {};

    currentUserToken: string = !_isEmpty(appStorageUtil.getCookiesItem(appStorageUtil.AUTH_TOKEN)) ? appStorageUtil.getCookiesItem(appStorageUtil.AUTH_TOKEN) : '';

    currentUserRoles: string[] = !_isEmpty(appStorageUtil.getCookiesItem(appStorageUtil.AUTH_TOKEN)) ? appStorageUtil.parseJWT(appStorageUtil.getAuthorizationToken()).scopes || [] : []

    currentUserTnc: boolean = !_isEmpty(appStorageUtil.getCookiesItem(appStorageUtil.AUTH_TOKEN)) ? appStorageUtil.parseJWT(appStorageUtil.getAuthorizationToken()).tnc || [] : []

    currentUserSubscription: string[] = !_isEmpty(appStorageUtil.getCookiesItem(appStorageUtil.AUTH_TOKEN)) ? appStorageUtil.parseJWT(appStorageUtil.getAuthorizationToken()).subscriptions || [] : []

    loading: boolean = false;

    get currentLoggedUserInState() {
        return this.currentUser;
    }

    get getCurrentUserRolesInState() {
        return this.currentUserRoles;
    }

    get getTokenInState() {
        return this.currentUserToken;
    }

    get getCurrentUserTnc() {
        return this.currentUserTnc;
    }

    get getCurrentUserSubscription() {
        return this.currentUserSubscription;
    }

    @Mutation
    SET_CURRENT_USER(newValue: object) {
        this.currentUser = newValue || {};
    }

    @Mutation
    SET_CURRENT_USER_ROLES(roles: string[]) {
        this.currentUserRoles = roles || [];
    }

  @Mutation
    SET_CURRENT_USER_TOKEN(token: string) {
        this.currentUserToken = token || '';
    }

    @Mutation
  SET_CURRENT_USER_TNC(tnc: boolean) {
      this.currentUserTnc = tnc;
  }

    @Mutation
    SET_CURRENT_SUBSCRIPTION(subscriptions: string[]) {
        this.currentUserSubscription = subscriptions;
    }

    @Mutation
    LOADING(newValue: boolean) {
        this.loading = newValue;
    }

    @Action({ rawError: true })
    logIn({ user, pwd, system_tag, turnstile_token }: {user: string; pwd: string; system_tag: string; turnstile_token?: string}) {
        const lang = i18n.i18next.language;
        return new Promise((resolve, reject) => {
            sendUAPost(`${authAPI.login}`, {
                user,
                pwd,
                system_tag,
                username: user,
                password: pwd,
                turnstile_token,
            }).then((response) => {
                if (response.status === 206) {
                    resolve(response);
                    return;
                }

                applyAuthToken(getAuthorizationFromResponse(response), this.context);
                appStorageUtil.setSessionItem('user', { user });
                this.context.commit('SET_CURRENT_USER', { user, pwd });
                IntroductionStore.getFilteredBeginnersGuide(new Payload({ language: lang }));
                resolve(response);
            }, (error) => {
                reject(error.response);
            });
        });
    }

    @Action({ rawError: true })
    verifyOtp(payload: Payload) {
        return new Promise((resolve, reject) => {
            sendUAPost(authAPI.verifyOtp, payload.data).then((response) => {
                applyAuthToken(getAuthorizationFromResponse(response), this.context);
                resolve(response);
            }, (error) => {
                reject(error.response);
            });
        });
    }

    @Action({ rawError: true })
    resendOtp(payload: Payload) {
        return new Promise((resolve, reject) => {
            sendUAPost(authAPI.resendOtp, payload.data).then((response) => {
                resolve(response);
            }, (error) => {
                reject(error.response);
            });
        });
    }

  @Action({ rawError: true })
    logout() {
        appStorageUtil.removeItem(appStorageUtil.CURRENT_USER);
        appStorageUtil.removeItem(IS_ADMIN);
        Cookies.remove(appStorageUtil.AUTH_TOKEN);
        Cookies.remove(appStorageUtil.AUTH_TOKEN_LEGACY);
        this.context.commit('SET_CURRENT_USER', {});
        this.context.commit('SET_CURRENT_USER_ROLES', []);
        this.context.commit('SET_CURRENT_USER_TOKEN', '');
        this.context.commit('SET_CURRENT_USER_TNC', false);
        this.context.commit('SET_CURRENT_SUBSCRIPTION', '');
        appStorageUtil.setItem(appStorageUtil.BANNER_IS_CLOSED, false);
        appStorageUtil.removeSessionItem(SHOW_BG);
    }

  @Action({ rawError: true })
  getTokenByAffID(affID: number) {
      return new Promise((resolve, reject) => {
          sendGet(`${authAPI.getTokenByAffID}/${affID}`, {}).then((response) => {
              resolve(response);
          }, (error) => {
              reject(error.response);
          });
      });
  }

  @Action({ rawError: true })
  getMyDetails(payload: Payload = new Payload()) {
      return new Promise((resolve, reject) => {
          sendGet(authAPI.me, {}, true).then((response) => {
              appStorageUtil.setItem(appStorageUtil.CURRENT_USER, response.data || {});
              this.context.commit('SET_CURRENT_USER', response.data);
              resolve(response);
          }, (error) => {
              reject(error.response);
          });
      });
  }

  @Action({ rawError: true })
  getMyInfo(payload: Payload = new Payload()) {
      return new Promise((resolve, reject) => {
          sendGet(authAPI.me, {}, true).then((response) => {
              resolve(response);
          }, (error) => {
              reject(error.response);
          });
      });
  }

  @Action({ rawError: true })
  updateMyInfo(payload: Payload) {
      return new Promise((resolve, reject) => {
          sendPut(authAPI.updateInfo, payload.data, true).then((response) => {
              resolve(response);
          }, (error) => {
              reject(error.response);
          });
      });
  }

  @Action({ rawError: true })
  saveAffiliateTerms(payload: Payload) {
      return new Promise((resolve, reject) => {
          sendPut(authAPI.agreeToTerms, payload.data, true).then((response) => {
              resolve(response);
          }, (error) => {
              reject(error.response);
          });
      });
  }
}

export default getModule(AuthorizationStore);
